/**
 * The client's calendar.
 *
 * ── Real grid semantics ─────────────────────────────────────────────────────────────────────
 * `role="grid"`, and every row owns its `rowheader` and its seven `gridcell`s DIRECTLY. The seven
 * cells are drawn inside a box of their own — the seven-column grid — and that box is
 * `role="presentation"`, which drops it from the accessibility tree and re-parents the cells onto
 * the row. Left generic it was a row whose only child was an unnamed group, and a screen reader
 * read no calendar at all.
 *
 * ── One tab stop, arrows inside it, Enter into a day ────────────────────────────────────────
 * `shared/lib/useGridFocus`, the same hook `StartMonthPicker` two panes to the left uses, and the
 * published grid pattern. The stop rides the CELLS, because a cell is what a session is dropped on
 * and what an arrow means; Enter or Space steps into that day's chips, where Space lifts and
 * Escape steps back out. A stop per chip was 36 presses to the footer on a five-week plan.
 *
 * While focus is inside the chips the hook stands DOWN: the arrows there aim a lift, and both
 * handlers answering one press moved the stop out from under the lift and cancelled it on the blur.
 *
 * ── Nothing on this grid moves ──────────────────────────────────────────────────────────────
 * The walking frame that used to hop across week one is gone by the owner's decision of 22
 * September 2026. The illustration still walks while the trainer is choosing; the client's own
 * schedule is read, not watched. `shared/ui/planSystemViz` keeps the hop for the ambient state.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MS_PER_DAY, addDays, parseIsoDate, toIsoDate } from '~/shared/lib/calendarDate';
import { useGridFocus } from '~/shared/lib/useGridFocus';

import type { ProjectionMove } from '../lib/useProjectionMove';
import type { AssignFlow } from '../lib/useAssignFlow';
import type { AssignProjection, ProjectionChip } from '../model/projectionGrid';

import { OccurrenceChip } from './OccurrenceChip';
import { OccurrenceEditPopover } from './OccurrenceEditPopover';
import { ProjectionCell } from './ProjectionCell';
import styles from './AssignPlanDialog.module.css';

interface ProjectionCalendarProps {
  readonly projection: AssignProjection;
  readonly move: ProjectionMove;
  readonly flow: AssignFlow;
}

export function ProjectionCalendar({ projection, move, flow }: ProjectionCalendarProps) {
  const { t } = useTranslation();
  const grid = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<{ chip: ProjectionChip; anchor: HTMLElement } | null>(null);
  const [inChips, setInChips] = useState(false);

  const navigation = useGridFocus({
    gridRef: grid,
    rowCount: projection.weeks.length,
    columnCount: 7,
    disabled: inChips,
  });

  /**
   * The grid as ONE run of days, which is the position space a keyboard lift walks.
   *
   * `useKeyboardMove` counts positions and clamps to them, so an arrow cannot aim at a day that is
   * not drawn — the bounds are the grid's own length rather than a pair of dates compared by hand.
   */
  const firstDay = parseIsoDate(projection.weeks[0]?.cells[0]?.iso ?? null);
  const dayCount = projection.weeks.length * 7;
  // Rounded, never truncated: a spring-forward week holds a 23-hour day, so the raw quotient is
  // 6.958 rather than 7 and the floor silently loses a day twice a year.
  const dayIndexOf = (chip: ProjectionChip) => {
    const day = parseIsoDate(chip.iso);
    return firstDay === null || day === null
      ? 0
      : Math.round((day.getTime() - firstDay.getTime()) / MS_PER_DAY);
  };
  const isoOfDay = (dayIndex: number) =>
    firstDay === null ? null : toIsoDate(addDays(firstDay, dayIndex));

  return (
    <>
      <div
        ref={grid}
        className={styles.calendar}
        role="grid"
        aria-label={t('web:assign.live.gridLabel')}
        onKeyDown={navigation.onKeyDown}
      >
        <div className={styles.calendarHead} role="row">
          {/* The rail's column has no name to give, but it is still a column: skipping the header
            * would put the seven weekday names one column out of step with the cells below. */}
          <span className={styles.calendarRail} role="columnheader" />
          {/* The same seven-column box the weeks use, for the same reason and with the same role:
            * dropped straight into the head's two-column grid the seven names wrapped into four
            * rows of two and stood over nothing. `role="presentation"` re-parents them onto the
            * row, so the head is one row of eight column headers however it is boxed. */}
          <div className={styles.calendarCells} role="presentation">
            {projection.weekdayHeads.map((head) => (
              <span key={head} className={styles.calendarWeekday} role="columnheader">
                {head}
              </span>
            ))}
          </div>
        </div>

        {projection.weeks.map((row, index) => (
          <div key={row.key} className={styles.calendarRow} role="row">
            <span className={styles.calendarRail} role="rowheader">
              {row.label}
            </span>
            <div className={styles.calendarCells} role="presentation">
              {row.cells.map((cell, column) => (
                <ProjectionCell
                  key={cell.iso}
                  cell={cell}
                  canDrop={move.canDrop}
                  row={index}
                  column={column}
                  focused={navigation.focus.row === index && navigation.focus.column === column}
                  onCellFocus={() => {
                    navigation.onCellFocus(index, column);
                    setInChips(false);
                  }}
                  onChipFocus={() => {
                    setInChips(true);
                  }}
                  aim={
                    move.lift?.iso !== cell.iso
                      ? 'none'
                      : (move.lift.verdict?.ok ?? true)
                        ? 'allowed'
                        : 'refused'
                  }
                >
                  {(chip) => (
                    <OccurrenceChip
                      key={chip.key}
                      chip={chip}
                      lifted={move.lift?.key === chip.key}
                      dayCount={dayCount}
                      dayIndex={dayIndexOf(chip)}
                      onLift={() => {
                        move.start(chip);
                      }}
                      onCancel={() => {
                        move.cancel(chip);
                      }}
                      onDrop={(dayIndex) => {
                        move.drop(chip, isoOfDay(dayIndex) ?? chip.iso);
                      }}
                      onAim={(dayIndex) => {
                        const iso = isoOfDay(dayIndex);
                        if (iso !== null) move.aim(chip, iso);
                      }}
                      onOpen={(anchor) => {
                        setOpen({ chip, anchor });
                      }}
                    />
                  )}
                </ProjectionCell>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.legend}>
        <span>
          <i className={styles.legendPlanned} aria-hidden="true" />
          {t('web:assign.live.legendPlanned')}
        </span>
        <span>
          <i className={styles.legendWithheld} aria-hidden="true" />
          {t('web:assign.live.legendWithheld')}
        </span>
        <span>{projection.movedNote ?? t('web:assign.live.legendDrag')}</span>
      </div>

      {open !== null && (
        <OccurrenceEditPopover
          chip={open.chip}
          fields={flow.editFieldsFor(open.chip)}
          anchor={open.anchor}
          onSave={(next) => flow.editOccurrence(open.chip, next)}
          onRestore={() => {
            flow.restoreOccurrence(open.chip);
          }}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
    </>
  );
}
